import {
  Dimensions,
  Image,
  PixelRatio,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as AppConstants from './AppConstants';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';
import * as Linking from 'expo-linking';
import { UAParser } from 'ua-parser-js';
import DeviceInfo from 'react-native-device-info';
import * as Network from 'expo-network';
import { Link } from '@react-navigation/native';

const MAX_DEBUG = 1000000;
const MAX_CONSOLE_LOG_SIZE = 9500;
const HOST = "findhumane.com";
const maxRemoteErrors = 5;
const maxLocalErrors = 10;
const storageKeyLocalData = "localData";
const FETCH_TIMEOUT_MILLISECONDS_MAIN = 60000;

let accumulatedDebug = "";
let urlMappings = {};
let errorsProcessed = 0;
let localData = {};
let debugLevelsRevert = null;

// https://www.aspca.org/labelguide
// https://www.aspca.org/shopwithyourheart/consumer-resources/meat-eggs-and-dairy-label-guide
const LABEL_GUIDES = <Text>We recommend reviewing the label guides from the {createLink("American Society for the Prevention of Cruelty to Animals (ASPCA)", "https://aspca.org/labelguide")}, the {createLink("Animal Welfare Institute (AWI)", "https://awionline.org/sites/default/files/publication/digital_download/AWI-Consumers-Guide-Food-Labels-Animal-Welfare.pdf")}, {createLink("Farm Forward", "https://www.farmforward.com/label-guide/")}, {createLink("Food Animal Concerns Trust", "https://www.foodanimalconcernstrust.org/humane-food-labels")}, and {createLink("Compassion in World Farming", "https://www.ciwf.com/your-food/learn-the-labels/")}.</Text>;

let DEBUGLEVEL = {
  ads: AppConstants.DEFAULT_DEBUG_LEVEL,
  certifications: AppConstants.DEFAULT_DEBUG_LEVEL,
  dataLocations: AppConstants.DEFAULT_DEBUG_LEVEL,
  filter: AppConstants.DEFAULT_DEBUG_LEVEL,
  local: AppConstants.DEFAULT_DEBUG_LEVEL,
  location: AppConstants.DEFAULT_DEBUG_LEVEL,
  map: AppConstants.DEFAULT_DEBUG_LEVEL,
  product: AppConstants.DEFAULT_DEBUG_LEVEL,
  network: AppConstants.DEFAULT_DEBUG_LEVEL,
  relativelyImportant: AppConstants.DEFAULT_DEBUG_LEVEL || AppConstants.IS_DEVELOPMENT,
  search: AppConstants.DEFAULT_DEBUG_LEVEL,
  state: AppConstants.DEFAULT_DEBUG_LEVEL,
  warnings: true, // Always keep warnings by default
};

const EXTRA_DEBUGLEVELS = {
  search: true,
};

function setExtraDebug(toggle) {
  if (toggle) {
    debugLevelsRevert = DEBUGLEVEL;
    for (const [k, v] of Object.entries(EXTRA_DEBUGLEVELS)) {
      DEBUGLEVEL[k] = v;
    }
  } else {
    DEBUGLEVEL = debugLevelsRevert;
  }
}

function appendToLog(str) {
  accumulatedDebug = accumulatedDebug + "\n" + str;

  // Limit how much memory debug is storing
  if (accumulatedDebug.length > MAX_DEBUG) {
    accumulatedDebug = "[... truncated ...] " + accumulatedDebug.substring(MAX_DEBUG / 2);
  }
}

function getLogPrefix() {
  return "[" + new Date().toISOString() + "] ";
}

function info(message, skipConsole) {
  const str = getLogPrefix() + message;
  if (!skipConsole) {
    console.log(str);
  }
  appendToLog(str);
}

function toJSONPretty(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    info("Error in toJSONPretty for " + (typeof obj) + " : " + e);
  }
}

function getMillisecondsAsSeconds(ms) {
  const millisecondsFloat = ms / 1000.0;
  let result = millisecondsFloat.toFixed(2);
  if (result.endsWith("00")) {
    result = result.substring(0, result.length - 3);
  }
  return result;
}

function getRootUri() {
  if (AppConstants.IS_DEVELOPMENT) {
    if (Platform.OS === "web") {
      return "http://localhost:3000/";
    } else {
      return "http://10.0.2.2:3000/";
    }
  } else {
    return "https://" + HOST + "/";
  }
}

function debug(message) {
  const str = getLogPrefix() + message;
  if (str.length > MAX_CONSOLE_LOG_SIZE) {
    console.debug(str.substr(0, MAX_CONSOLE_LOG_SIZE) + " [...] TRUNCATED");
  } else {
    console.debug(str);
  }
  appendToLog(str);
}

function error(e, context) {
  if (e) {
    if (context) {
      info("ERROR (" + context + "): " + getErrors(e));
    } else {
      info("ERROR: " + getErrors(e));
      dir(e);
    }
  }
}

let LOGICAL_USER_AGENT = null;
try {
  LOGICAL_USER_AGENT = UAParser();
} catch (e) {
  debug("Error parsing user agent: " + e);
}

function dir(obj) {
  try {
    if (isWeb()) {
      console.dir(obj);
    } else {
      console.debug(toJSONPretty(obj));
    }
    appendToLog(toJSONPretty(obj));
  } catch (e) {
    appendToLog("Error in dir: " + e);
  }
}

function isAndroid() {
  return Platform.OS === "android";
}

function isIOS() {
  return Platform.OS === "ios";
}

function isWeb() {
  return Platform.OS === "web";
}

function isWebAndroid() {
  if (isWeb()) {
    if (LOGICAL_USER_AGENT) {
      if (LOGICAL_USER_AGENT.os && LOGICAL_USER_AGENT.os.name && LOGICAL_USER_AGENT.os.name.startsWith("Android")) {
        return true;
      }
    }
  }
  return false;
}

function isWebiOS() {
  if (isWeb()) {
    if (LOGICAL_USER_AGENT) {
      if (LOGICAL_USER_AGENT.os && LOGICAL_USER_AGENT.os.name && LOGICAL_USER_AGENT.os.name.startsWith("iOS")) {
        return true;
      }
    }
  }
  return false;
}

function handleException(
  e,
  source,
  snackContainer,
  additionalSetState = {
  },
) {
  handleErrors({
    explicitErrors: e,
    source: source,
    snackContainer: snackContainer,
    additionalSetState: additionalSetState,
  });
}

function reportIssue(message) {
  handleErrors({
    explicitErrors: message,
  });
}

function getClass(obj) {
  return obj && obj.constructor ? obj.constructor.name : "Unknown class";
}

// Either specify an explicit object or array in errors,
// or specify an errorsSource (such as a fetch response)
// that will be checked for various well-known error properties
// such as errorsSource.body.messages, errorsSource.errors
//
// If snackContainer is set, call setState({ snackVisible: true, snackText: errors, processing: false, ...additionalSetState });
// If errorsContainer is set, call setState({ errors: errors, processing: false, ...additionalSetState });
//
// If remoteLog is true, POST debug back to /api/debug
//
// We use console.log instead of console.error because Expo considers console.error as fatal
function handleErrors({
  explicitErrors = null,
  errorsSource = null,
  snackContainer = null,
  errorsContainer = null,
  additionalSetState = {
  },
  remoteLog = true,
  remoteData = null,
  source = "unknown",
} = {}) {

  errorsProcessed++;

  if (remoteLog && errorsProcessed > maxRemoteErrors) {
    // Don't want to overwhelm the network with error reports
    remoteLog = false;
  }

  if (errorsProcessed > maxLocalErrors) {
    //console.log(getLogPrefix() + "handleErrors: Reached maxLocalErrors");
    return;
  }

  try {
    // Before we do anything risky, log the errors to the console, if available
    let errors = "Unknown error";
    let stacks = "";
    if (explicitErrors) {
      errors = explicitErrors;
    } else if (errorsSource) {
      if (errorsSource.body && errorsSource.body.messages) {
        errors = errorsSource.body.messages;
      } else if (errorsSource.errors) {
        errors = errorsSource.errors;
      } else if (errorsSource.componentDidCatch) {
        errors = [errorsSource.componentDidCatch.error, errorsSource.componentDidCatch.errorInfo];
        if (errorsSource.componentDidCatch.errorInfo && typeof errorsSource.componentDidCatch.errorInfo === 'object') {
          for (let prop in errorsSource.componentDidCatch.errorInfo) {
            errors += prop + "=" + errorsSource.componentDidCatch.errorInfo[prop];
          }
        }
      }
    }

    let allErrors = getLogPrefix() + "handleErrors: Received errors (" + source + ")";

    if (errors) {
      allErrors += ": ";
      if (Array.isArray(errors)) {
        console.log(allErrors);
        console.log(errors);
        for (x of errors) {
          const getErrorsResult = getErrors(x, null, true);
          allErrors += "\n\nDetails: " + getErrors(x, null, true);
          console.log(getErrorsResult);
        }
      } else {
        const getErrorsResult = getErrors(errors, null, true);
        console.log(allErrors);
        console.log(errors);
        console.log(getErrorsResult);
        allErrors += "\n\nDetails: " + getErrorsResult;
      }
    }

    // Append the errors to our log
    appendToLog(allErrors);
    appendToLog(getLogPrefix() + "Finished handleErrors processing");

    if (remoteLog) {
      // Finally, we want to send this debug back to the server

      if (source) {
        allErrors += "\n\nSource: " + source;
      }

      // Add various context if we find it
      if (errorsSource) {
        if (errorsSource.status) {
          allErrors += "\n\nStatus: " + errorsSource.status;
        }
      }

      // Try to add in the state
      //try {
        // Causes issues serializing a lot of state
        //allErrors += "\n\nState: " + toJSONPretty(store.getState());
      //} catch (innerException) {
      //  allErrors += "\n\nState: Could not serialize: " + innerException;
      //}

      if (remoteData) {
        try {
          allErrors += "\n\nremoteData: " + toJSONPretty(remoteData);
        } catch (innerException) {
          allErrors += "\n\nremoteData: Could not serialize: " + innerException + " ; " + remoteData;
        }
      }

      allErrors += "\n\nLog:\n" + accumulatedDebug;

      sendDebug(allErrors);
    }
  } catch (e) {
    console.log(getLogPrefix() + "handleErrors: Error processing", e);
  }
}

function getErrors(e, detailed, includeMaximumDetails) {
  let result = "";
  if (e) {
    result = e.toString();
    try {
      if (e.message && !result.endsWith(e.message)) {
        result += " (message: " + e.message + ")";
      }
      if (e.status && !result.endsWith("Error " + e.status)) {
        result += " (status: " + e.status + ")";
      }
      if (e.code) {
        result += " (code: " + e.code + ")";
      }
      if (e.detailCode) {
        result += " (detailCode: " + e.detailCode + ")";
      }
      if (e.nativeEvent && e.nativeEvent.error) {
        result += " (nativeEvent.error: " + e.nativeEvent.error + ")";
      }
      if (detailed && e.messages_list && e.messages_list.length > 0) {
        result += " (messages list: " + e.messages_list.join("; ") + ")";
      }
      if (detailed && e.fileName) {
        result += " (file: " + e.fileName + ")";
      }
      if (detailed && e.lineNumber) {
        result += " (line: " + e.lineNumber + ")";
      }
      if (detailed && e.columnNumber) {
        result += " (column: " + e.columnNumber + ")";
      }
      if (detailed && e.name) {
        result += " (class: " + e.name + ")";
      } else if (detailed && e.constructor) {
        result += " (class: " + e.constructor.name + ")";
      }
      if (detailed && e.stack) {
        result += " (stack: " + e.stack + ")";
      }
      if (detailed && e.underlyingException) {
        result += " (underlying: " + getErrors(e.underlyingException, detailed) + ")";
      }

      if (includeMaximumDetails) {
        result += " (class: " + getClass(e) + ")";
      }

      // Replace some common text
      result = result.replaceAll("Error: Error", "Error");
      result = result.replaceAll("Network request failed", "Check your internet connection");
      result = result.replaceAll("The operation was aborted.", "Timed out after " + getMillisecondsAsSeconds(FETCH_TIMEOUT_MILLISECONDS_MAIN) + " seconds");
    } catch (innerException) {
      result += " (failed processing: " + innerException.toString() + ")";
    }
  } else {
    result = "Unexpected error";
  }
  return result;
}

async function sendDebugDirect(msg) {
  try {

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000 /* ms */);

    const result = await fetch(
      getRootUri() + "api/debug",
      {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Host": HOST,
        },
        body: JSON.stringify({
          message: msg,
        }),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    return result;

  } catch (e) {
    info("sendDebugDirect failed with: " + getErrors(e));
    return null;
  }
}


// We use console.log instead of console.error because Expo considers console.error as fatal
async function sendDebug(msg) {
  try {
    console.log(getLogPrefix() + "sendDebug: Started");
    await sendDebugDirect(msg);
    console.log(getLogPrefix() + "sendDebug: Succeeded calling api/debug");
  } catch (e) {
    console.log(getLogPrefix() + "sendDebug: Failed call to api/debug", e);
  }
}

async function getStoredValue(key) {
  return await getStoredValueInsecure(key);
}

async function getStoredValueSecure(key) {
    if (Platform.OS === "web") {
    return await AsyncStorage.getItem(key);
  } else {
    return await SecureStore.getItemAsync(key);
  }
}

async function getStoredValueInsecure(key) {
  if (isWeb()) {
    return await AsyncStorage.getItem(key);
  } else {
    return await AsyncStorage.getItem(key);
  }
}

async function setStoredValue(key, val) {
  return await setStoredValueInsecure(key, val);
}

async function setStoredValueSecure(key, val) {
  if (Platform.OS === "web") {
    return await setStoredValueWeb(key, val);
  } else {
    // https://docs.expo.dev/versions/latest/sdk/securestore/
    // Size limit for a value is 2048 bytes
    return await SecureStore.setItemAsync(key, val);
  }
}

async function setStoredValueInsecure(key, val) {
  if (isWeb()) {
    return await setStoredValueWeb(key, val);
  } else {
    // https://react-native-async-storage.github.io/async-storage/docs/limits/
    // 
    // Total storage size is capped at 6 MB by default. You can increase this size
    // by specifying a new size using feature flag.
    // 
    // Per-entry is limited by a size of a WindowCursor, a buffer used to read data
    // from SQLite. Currently it's size is around 2 MB. This means that the single
    // item read at one time cannot be larger than 2 MB. There's no supported
    // workaround from AsyncStorage. We suggest keeping your data lower than that,
    // by chopping it down into many entries, instead of one massive entry. This
    // is where multiGet and multiSet APIs can shine.
    return await AsyncStorage.setItem(key, val);
  }
}

async function setStoredValueWeb(key, val) {
  // Limits:
  //
  // Android: https://react-native-async-storage.github.io/async-storage/docs/limits
  // "This means that the single item read at one time cannot be larger than 2 MB"
  // "Total storage size is capped at 6 MB by default"
  //
  // Web: https://en.wikipedia.org/wiki/Web_storage#Storage_size
  // "Safari 8 allows 5 MB'"
  //
  return await AsyncStorage.setItem(key, val);
}

async function removeStoredValue(key) {
  if (isWeb()) {
    return await AsyncStorage.removeItem(key);
  } else {
    return await SecureStore.deleteItemAsync(key);
  }
}

async function loadLocalData() {
  const rawLocalData = await getStoredValue(storageKeyLocalData);
  if (rawLocalData) {
    localData = JSON.parse(rawLocalData);
    if (DEBUGLEVEL.local) debug("localData: " + toJSONPretty(localData));
  } // else localData is initialized to {} so nothing to do
  return localData;
}

async function updateLocalData(key, val) {
  localData[key] = val;
  await setStoredValue(storageKeyLocalData, toJSONPretty(localData));
}

async function removeLocalData() {
  localData = {};
  await removeStoredValue(storageKeyLocalData);
}

async function updateSavedAppState(appStateKey, appStateValue) {
  info("AppUtilities.updateSavedAppState updating " + appStateKey);

  let savedAppState = localData[AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE];
  if (!savedAppState) {
    savedAppState = {};
  }
  savedAppState[appStateKey] = appStateValue;
  await updateLocalData(AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE, savedAppState);
}

function getSavedAppState(appStateKey, defaultValue) {
  let savedAppState = localData[AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE];
  if (!savedAppState) {
    savedAppState = {};
  }

  let result = savedAppState[appStateKey];
  if (!result) {
    result = defaultValue;
  }
  return result;
}

function isUndefined(o) {
  return typeof o === 'undefined';
}

function isDefined(o) {
  return !isUndefined(o);
}

function isString(o) {
  return typeof o === 'string';
}

function isNull(obj) {
  return obj === null;
}

function isBlank(str) {
  return isUndefined(str) || isNull(str) || str.length == 0;
}

function isNullOrUndefined(x) {
  return isNull(x) || isUndefined(x);
}

function getLocalData(key, defaultValue) {
  let result = localData[key];
  if (isNullOrUndefined(result)) {
    result = defaultValue;
  }
  return result;
}

function getAllLocalData() {
  return localData;
}

async function login(responseJson) {
  responseJson.expires = new Date().getTime() + (responseJson.expires_in * 1000);
  await updateLocalData(AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION, responseJson);
}

function getLocalDataRemoteSession() {
  return localData[AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION];
}

async function logout() {
  if (getLocalData(AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION)) {
    await clearLogInData();
  }
}

async function clearLogInData() {
  debug("clearLogInData entry");
  await updateLocalData(AppConstants.LOCAL_DATA_KEYS.REMOTE_SESSION, null);
  debug("clearLogInData exit");
}

function isAccessTokenExpired() {
  if (getLocalDataRemoteSession()) {
    const x = new Date(getLocalDataRemoteSession().expires);
    const y = new Date();
    if (DEBUGLEVEL.network) debug("isAccessTokenExpired expires: " + x + ", now: " + y);
    return x < y;
  } else {
    return true;
  }
}

async function ensureValidAccessToken() {
  if (DEBUGLEVEL.network) debug("> ensureValidAccessToken " + toJSONPretty(getLocalDataRemoteSession()));

  if (isAccessTokenExpired()) {

    const url = getRootUri() + "api/refresh_token";

    debug("ensureValidAccessToken: Requesting token refresh from " + url);

    let result = null;

    try {
      const response = await fetch(
        url,
        {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Host": HOST,
          },
          body: toJSONPretty({
            "refresh_token": getLocalDataRemoteSession().refresh_token,
          }),
        }
      );
      
      if (DEBUGLEVEL.network) debug("refresh_token raw response: " + toJSONPretty(response));
  
      const responseJson = await response.json();
  
      if (DEBUGLEVEL.network) debug("refresh_token JSON response: " + toJSONPretty(responseJson));
  
      if (responseJson.result) {
        await login(responseJson);
        result = { successful: true, token: getLocalDataRemoteSession().token };
      } else {
        // If there's some problem with the refresh token, require a relogin
        if (responseJson.status == 403) {
          // This can happen, for example, if the app logs into development
          // and then tries to log into production. In that case, we think
          // the token is still not expired, but when we try to use it,
          // it fails. It could also happen if the token database is cleared
          // for some reason. So in this case, just log the user out and
          // have them log back in.
          if (DEBUGLEVEL.network) debug("ensureValidAccessToken: 403; logging out");
  
          await logout();
  
          result = { successful: false, errors: responseJson.messages, status: responseJson.status, failedRefresh: true, redirected: true };
        } else {
          result = { successful: false, errors: responseJson.messages, status: responseJson.status, failedRefresh: true };
        }
      }
    } catch (error) {
      result = { successful: false, errors: ["Error: " + error] };
    }

    debug("ensureValidAccessToken: Token refresh result: successful: " + result.successful + ", token: " + result.token);

    return result;
  }
  if (DEBUGLEVEL.network) debug("< ensureValidAccessToken token is valid");
  return { successful: true, token: getLocalDataRemoteSession().token };
}

async function fetchAuthorized(context, uri, method, postJson, formUrlEncoded, skipAudit, skipErrorStatusHandling) {
  return await fetchCustomized(context, uri, method, postJson, true, formUrlEncoded, skipAudit, skipErrorStatusHandling);
}

async function fetchUnauthorized(context, uri, method, postJson, formUrlEncoded, skipAudit, skipErrorStatusHandling) {
  return await fetchCustomized(context, uri, method, postJson, false, formUrlEncoded, skipAudit, skipErrorStatusHandling);
}

function handleErrorStatus(status, responseText) {
  if (DEBUGLEVEL.warnings) debug("handleErrorStatus response error: " + responseText);
  
  let messages_list = [];
  let message = "Error " + status;
  let detailCode = 0;

  try {
    let responseJson = JSON.parse(responseText);

    if (DEBUGLEVEL.warnings) debug("handleErrorStatus error response JSON: " + toJSONPretty(responseJson));

    if (responseJson) {
      if (responseJson.messages) {
        messages_list = responseJson.messages;
      } else if (responseJson.message) {
        message = responseJson.message;
      } else if (responseJson.error) {
        message = responseJson.error;
      } else {
        if (responseText) {
          message += " (" + responseText + ")";
        }
      }

      if ("detailCode" in responseJson) {
        detailCode = responseJson.detailCode;
      }
    } else {
      if (responseText) {
        message += " (" + responseText + ")";
      }
    }
  } catch (e) {
    if (responseText) {
      // This is usually HTML, so no reason
      // to add to the user message. It will
      // be logged above and sent in sendDebug
      //message += " (" + responseText + ")";
    }
  }

  if (message && message.length > 200) {
    message = message.substring(0, 200) + " ... ";
  }

  var error = new Error(message);
  error.status = status;
  error.messages_list = messages_list;
  error.detailCode = detailCode;
  if (DEBUGLEVEL.warnings) debug("handleErrorStatus error: " + toJSONPretty(error));
  throw error;
}

function isStatusSuccess(status) {
  return status >= 200 && status < 300;
}

async function fetchCustomized(context, uri, method, postJson, authorize, formUrlEncoded, skipAudit, skipErrorStatusHandling) {
  const urlMapping = urlMappings[uri];
  const actualRootUri = urlMapping && urlMapping.rootUri ? urlMapping.rootUri : getRootUri();
  const actualHost = urlMapping && urlMapping.host ? urlMapping.host : HOST;

  if (uri[0] == '/') {
    uri = uri.substring(1);
  }

  if (uri.indexOf("?v=") == -1 && uri.indexOf("&v=") == -1) {
    if (uri.indexOf('?') == -1) {
      uri += "?v=" + AppConstants.VERSION;
    } else {
      uri += "&v=" + AppConstants.VERSION;
    }
  }

  if (!method) {
    method = "GET";
  }

  info("> fetchCustomized " + method + " " + actualRootUri + uri + " (" + context + ")");

  if (postJson) {

    let debugJson = postJson;

    if ("password" in postJson) {
      debugJson = Object.assign({}, postJson);
      delete debugJson.password;
    }

    if (!skipAudit) {
      dir(debugJson);
    }
  }

  let result = { successful: false };
  try {
    if (DEBUGLEVEL.network) debug("fetchCustomized authorize: " + authorize + ", formUrlEncoded: " + toJSONPretty(formUrlEncoded));

    let ready = true;

    if (authorize) {
      if (DEBUGLEVEL.network) debug("fetchCustomized calling ensureValidAccessToken");

      result = await ensureValidAccessToken();
      ready = result.successful;

      if (DEBUGLEVEL.network) debug("fetchCustomized ensureValidAccessToken returned " + ready);
    }
    
    if (ready) {
      let headers = {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Host": actualHost,
      };

      if (authorize) {
        headers["Authorization"] = "Bearer " + result.token;
      }

      const savedToken = authorize ? result.token : null;

      let fetchParams = {
        method: method,
        headers: headers,
      };

      if (formUrlEncoded) {
        headers["Content-Type"] = "application/x-www-form-urlencoded";
        fetchParams.body = formUrlEncoded.toString();
      } else if (postJson) {
        fetchParams.body = toJSONPretty(postJson);
      }

      // Timeout in RN v0.60.4; wait until it's available in Expo
      // https://github.com/facebook/react-native/commit/5e36b0c6eb2494cefd11907673aa018831526750
      //const controller = new AbortController();
      //const signal = controller.signal;
      //signal: signal,
      //const timeoutId = setTimeout(() => controller.abort(), 30000);
      //clearTimeout(timeoutId);

      try {

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MILLISECONDS_MAIN /* ms */);
        fetchParams.signal = controller.signal;

        const response = await fetch(
          actualRootUri + uri,
          fetchParams,
        );
  
        clearTimeout(timeoutId);

        const status = response.status;
  
        info("< fetchCustomized response: " + status + " (" + context + ")");
  
        const responseText = await response.text();
  
        if (isStatusSuccess(status)) {

          const responseBody = JSON.parse(responseText);

          if (DEBUGLEVEL.network) {
            debug("fetchCustomized response success:");
            dir(responseBody);
          }

          result = {
            status: status,
            successful: true,
            body: responseBody,
            token: savedToken,
          };
        } else {
          if (skipErrorStatusHandling) {
            result = {
              status: status,
              successful: false,
              responseText: responseText,
            };
          } else {
            handleErrorStatus(status, responseText);
          }
        }
      } catch (error) {
        if (DEBUGLEVEL.warnings) debug("< fetchCustomized error: url: " + uri + ", error: " + getErrors(error, true));

        result = {
          status: 500,
          successful: false,
          status: error.status,
          detailCode: "detailCode" in error ? error.detailCode : 0,
          errors: error.messages_list && error.messages_list.length > 0 ? error.messages_list : [ error.message ],
        };

        var finalError = new Error(result.errors.join(", "));
        finalError.status = result.status;
        finalError.detailCode = result.detailCode;
        finalError.errors = result.errors;
        finalError.underlyingException = error;
        throw finalError;
      }
    } else {
      if (DEBUGLEVEL.network) debug("fetchCustomized not ready");

      var finalError = new Error(result.errors.join(", "));
      finalError.status = result.status;
      finalError.detailCode = result.detailCode;
      finalError.errors = result.errors;
      throw finalError;
    }
  } catch (e) {
    if (e.errors) {
      throw e;
    } else {
      var finalError = new Error(e.toString());
      finalError.status = result.status;
      finalError.detailCode = result.detailCode;
      finalError.errors = result.errors;
      finalError.underlyingException = e;
      throw finalError;
    }
  }

  if (DEBUGLEVEL.network) {
    debug("< fetchCustomized result:");
    dir(result);
  }

  return result;
}

function getHeight() {
  return Dimensions.get("window").height;
}

function getWidth() {
  return Dimensions.get("window").width;
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getAccumulatedDebug() {
  return accumulatedDebug;
}

function getUUID() {
  return uuidv4();
}

function createRequestUrl(url) {
  if (url.includes("?")) {
    url += "&emulate_host=findhumane.com";
  } else {
    url += "?emulate_host=findhumane.com";
  }
  return url;
}

// [min, max]
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

function getHeaderRight(navigation) {
  return null;
}

function debugObject(obj, name) {
  try {
    info("debugObject stringifying " + name);
    for (var prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        try {
          JSON.stringify(obj[prop]);
          info("  debugObject " + prop + ": " + toJSONPretty(obj[prop]));
        } catch (e) {
          info("  debugObject could not stringify " + prop);
        }
      }
    }
  } catch (e) {
    handleException(e, "debugObject");
  }
}

function escapeHTML(unsafe) {
  if (isNullOrUndefined(unsafe)) {
    return "";
  } else {
    return unsafe.replace(
      /[\u0000-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u00FF]/g,
      c => '&#' + ('000' + c.charCodeAt(0)).slice(-4) + ';'
    );
  }
}

// compare
function areObjectsEqual(x, y) {
  for (var prop in x) {
    if (x[prop] !== y[prop]) {
      return false;
    }
  }
  for (var prop in y) {
    if (x[prop] !== y[prop]) {
      return false;
    }
  }
  return true;
}

function removeTemporaryParams(filter) {
  // Some params will be filtered out
  const result = Object.assign({}, filter);
  delete result[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]];
  return result;
}

function isDefaultFilterPasture(dataFilter) {
  return areObjectsEqual(removeTemporaryParams(dataFilter), AppConstants.DATA_FILTERS_BUILTIN_OPTIONS[AppConstants.DATA_FILTERS_BUILTIN_NAMES.PASTURE]);
}

function isDefaultFilterOutdoors(dataFilter) {
  return areObjectsEqual(removeTemporaryParams(dataFilter), AppConstants.DATA_FILTERS_BUILTIN_OPTIONS[AppConstants.DATA_FILTERS_BUILTIN_NAMES.OUTDOORS]);
}

function isDefaultFilterIndoors(dataFilter) {
  return areObjectsEqual(removeTemporaryParams(dataFilter), AppConstants.DATA_FILTERS_BUILTIN_OPTIONS[AppConstants.DATA_FILTERS_BUILTIN_NAMES.INDOORS]);
}

function getDefaultFilterPastureLink() {
  return "/find_humane/api/defaultMainDataPastureV" + AppConstants.MAJOR_VERSION_NUMBER + ".json";
}

function getDefaultFilterOutdoorsLink() {
  return "/find_humane/api/defaultMainDataOutdoorsPlusV" + AppConstants.MAJOR_VERSION_NUMBER + ".json";
}

function getDefaultFilterIndoorsLink() {
  return "/find_humane/api/defaultMainDataIndoorsPlusV" + AppConstants.MAJOR_VERSION_NUMBER + ".json";
}

function isDefaultFilter(dataFilter) {
  return isDefaultFilterPasture(dataFilter) || isDefaultFilterOutdoors(dataFilter) || isDefaultFilterIndoors(dataFilter);
}

function getDefaultFilterLink(dataFilter) {
  if (isDefaultFilterOutdoors(dataFilter)) {
    return getDefaultFilterOutdoorsLink();
  } else if (isDefaultFilterPasture(dataFilter)) {
    return getDefaultFilterPastureLink();
  } else if (isDefaultFilterIndoors(dataFilter)) {
    return getDefaultFilterIndoorsLink();
  }

  return null;
}

function getMainDataLink(dataFilter) {
  if (isDefaultFilterOutdoors(dataFilter)) {
    return getDefaultFilterOutdoorsLink();
  } else if (isDefaultFilterPasture(dataFilter)) {
    return getDefaultFilterPastureLink();
  } else if (isDefaultFilterIndoors(dataFilter)) {
    return getDefaultFilterIndoorsLink();
  }

  let result = "/find_humane/api/mainData.json?" + convertFilterHashToQueryParametersSorted(dataFilter);
  return result;
}

function getDefaultFilterExplorePastureLink() {
  return "/find_humane/api/defaultExplorePastureV" + AppConstants.MAJOR_VERSION_NUMBER + ".json";
}

function getDefaultFilterExploreOutdoorsLink() {
  return "/find_humane/api/defaultExploreOutdoorsPlusV" + AppConstants.MAJOR_VERSION_NUMBER + ".json";
}

function getDefaultFilterExploreIndoorsLink() {
  return "/find_humane/api/defaultExploreIndoorsPlusV" + AppConstants.MAJOR_VERSION_NUMBER + ".json";
}

function getDefaultFilterExploreLink(dataFilter) {
  if (isDefaultFilterOutdoors(dataFilter)) {
    return getDefaultFilterExploreOutdoorsLink();
  } else if (isDefaultFilterPasture(dataFilter)) {
    return getDefaultFilterExplorePastureLink();
  } else if (isDefaultFilterIndoors(dataFilter)) {
    return getDefaultFilterExploreIndoorsLink();
  }

  return null;
}

function getExploreLink(dataFilter) {
  if (isDefaultFilterOutdoors(dataFilter, AppConstants.DATA_FILTERS_BUILTIN_OPTIONS[AppConstants.DATA_FILTERS_BUILTIN_NAMES.OUTDOORS])) {
    return getDefaultFilterExploreOutdoorsLink();
  } else if (isDefaultFilterPasture(dataFilter, AppConstants.DATA_FILTERS_BUILTIN_OPTIONS[AppConstants.DATA_FILTERS_BUILTIN_NAMES.PASTURE])) {
    return getDefaultFilterExplorePastureLink();
  } else if (isDefaultFilterIndoors(dataFilter, AppConstants.DATA_FILTERS_BUILTIN_OPTIONS[AppConstants.DATA_FILTERS_BUILTIN_NAMES.INDOORS])) {
    return getDefaultFilterExploreIndoorsLink();
  }

  let result = "/find_humane/api/explore.json?" + convertFilterHashToQueryParametersSorted(dataFilter);
  return result;
}

function convertFilterHashToQueryParametersSorted(hash) {
  // https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/QueryStringParameters.html
  // "The order of parameters matters in query strings. [...] [different orders may cause CDNs] to cache two separate versions of the object"
  const finalHash = Object.assign({}, hash);
  finalHash["v"] = AppConstants.VERSION;

  let q = finalHash[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]];
  delete finalHash[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]];

  // Sending a unique ID on each query would break caches
  let id = finalHash[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]];
  delete finalHash[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]];

  const props = [];
  for (const prop in finalHash) {
    props.push(prop);
  }
  props.sort();
  let result = "";
  for (const prop of props) {
    if (result.length > 0) {
      result += "&";
    }
    result += prop + "=" + encodeURIComponent(finalHash[prop]);
  }
  if (!isBlank(q)) {
    if (result.length > 0) {
      result += "&";
    }
    result += AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY] + "=" + encodeURIComponent(q);
  }
  return result;
}

// utmCampaign: The individual campaign name, slogan, promo code, etc. for a product.
// utmMedium (optional): The advertising or marketing medium, for example:
//   referral, affiliate, cpc, banner, email, social, newsletter, blog_comments, cpm, display, organic, ad (https://cxl.com/wp-content/uploads/2020/02/Screen-Shot-2020-02-28-at-12.06.14-PM.png)
//   Default: referral: "Used to identify traffic from websites not known to be a search engine or social media platform."
function getLinkWithUTM(link, utmCampaign, utmMedium) {
  if (link.indexOf("utm_source") == -1) {
    if (link.indexOf("?") == -1) {
      link += "?";
    } else {
      link += "&";
    }
    if (!utmMedium) {
      utmMedium = AppConstants.UTM_MEDIUMS.REFERRAL;
    }
    if (!utmCampaign) {
      utmCampaign = AppConstants.UTM_CAMPAIGNS.GENERAL;
    }
    link += "utm_source=" + AppConstants.UTM_SOURCES.FINDHUMANE;
    link += "&utm_medium=" + utmMedium;
    link += "&utm_campaign=" + utmCampaign;
  }
  return link;
}

function openLink(link, utmCampaign, utmMedium) {
  link = getLinkWithUTM(link, utmCampaign, utmMedium);
  info("openLink: " + link);
  if (isWeb()) {
    window.open(link, '_blank');
  } else {
    Linking.openURL(link);
  }
}

function createLink(
           children,
           link,
           utmCampaign,
           utmMedium,
           skipUTM,
           linkStyle,
           key
         ) {
  if (isWeb()) {
    if (link.startsWith("http") && !skipUTM) {
      link = getLinkWithUTM(link, utmCampaign, utmMedium);
    }
    const linkProps = {};
    if (linkStyle) {
      linkProps.style = linkStyle;
    }
    return (
      <a
        key={key}
        href={link}
        target="_blank"
        {...linkProps}
      >{children}</a>
    );
  } else {
    const linkProps = {};
    if (isString(children)) {
      return (
        <Text
          key={key}
          style={AppConstants.STYLES.link}
          onPress={() => openLink(link, utmCampaign, utmMedium)}
          {...linkProps}
        >{children}</Text>
      );
    } else {
      return (
        <TouchableOpacity
          key={key}
          onPress={() => openLink(link, utmCampaign, utmMedium)}
          {...linkProps}
        >{children}</TouchableOpacity>
      );
    }
  }
}

function createInternalLink(navigation, children, screen, params, linkStyle) {
  // Using a normal link causes navigation history to be lost on web
  if (isWeb() && false) {
    let link = AppConstants.SCREEN_LINKS[screen];
    if (params) {
      for (const key of Object.keys(params)) {
        const regex = new RegExp(":" + key);
        link = link.replace(regex, params[key]);
      }
    }
    const linkProps = {};
    if (linkStyle) {
      linkProps.style = linkStyle;
    }

    return (
      <a
        href={link}
        {...linkProps}
      >{children}</a>
    );
  } else {
    if (isString(children)) {
      return (
        <Text
          style={AppConstants.STYLES.link}
          onPress={() => navigation.navigate(screen, params)}
        >{children}</Text>
      );
    } else {
      return (
        <TouchableOpacity
          onPress={() => navigation.navigate(screen, params)}
        >{children}</TouchableOpacity>
      );
    }
  }
}

function createNavigationLink(children, screen, params, style) {
  return (
    <Link to={{ screen: screen, params: params }} action="push" style={style}>
      { children }
    </Link>
  );
}

function filterDown(baseFilter, deeperFilter) {
  const result = Object.assign({}, baseFilter);
  if (AppConstants.CERTIFICATIONS_MAP[deeperFilter]) {
    Object.keys(AppConstants.CERTIFICATIONS_MAP).forEach((key) => {
      if (result[key] && key == deeperFilter) {
        // Keep it
      } else {
        result[key] = false;
      }
    });
  } else {
    result[deeperFilter] = true;
  }
  return result;
}

function getZoomLevelForGeolocationTypes(types) {
  // https://developers.google.com/maps/documentation/javascript/overview#MapOptions
  let zoomLevel = AppConstants.DEFAULT_ZOOMED;
  if (types && types.length > 0) {
    // https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types
    for (const resultType of types) {
      if (resultType == "street_address" ||
          resultType == "intersection" ||
          resultType == "premise" ||
          resultType == "subpremise" ||
          resultType == "intersection" ||
          resultType == "park" ||
          resultType == "point_of_interest"
         ) {
        zoomLevel = AppConstants.ZOOM_LEVELS.BUILDINGS - 4;
      } else if (resultType == "locality") {
        zoomLevel = AppConstants.ZOOM_LEVELS.CITY;
      } else if (resultType == "sublocality" || resultType == "postal_code") {
        zoomLevel = AppConstants.ZOOM_LEVELS.CITY + 2;
      } else if (resultType == "neighborhood" || resultType == "airport") {
        zoomLevel = AppConstants.ZOOM_LEVELS.CITY + 3;
      } else if (resultType == "country") {
        zoomLevel = 6;
      }
    }
  }
  return zoomLevel;
}

function checkSearchResults(resultsCount, showSnack) {
  if (resultsCount == 0) {
    showSnack("No results");
  }
}

function isMarkdownLink(markdown) {
  if (markdown) {
    if (markdown.startsWith("<http") && markdown.endsWith(">")) {
      return true;
    } else if (markdown.startsWith("[http") && markdown.endsWith(")")) {
      return true;
    }
  }
  return false;
}

function getMarkdownLink(markdown) {
  if (isMarkdownLink(markdown)) {
    if (markdown.startsWith("<http") && markdown.endsWith(">")) {
      return markdown.substring(1, markdown.length - 1);
    } else if (markdown.startsWith("[http") && markdown.endsWith(")")) {
      return markdown.substring(markdown.indexOf("(") + 1, markdown.lastIndexOf(")"));
    }
  }
  return null;
}

function getMarkdownLinkText(markdown) {
  if (isMarkdownLink(markdown)) {
    if (markdown.startsWith("<http") && markdown.endsWith(">")) {
      return markdown.substring(1, markdown.length - 1);
    } else if (markdown.startsWith("[http") && markdown.endsWith(")")) {
      return markdown.substring(1, markdown.lastIndexOf("]("));
    }
  }
  return null;
}

async function recordAppPageView(urlFragment) {

  // No-op for calls from the web as those page views
  // are transparently tracked
  if (isWeb()) {
    return;
  }

  info("recordAppPageView started for " + urlFragment);

  try {
    const userAgent = await getUserAgent();

    // Response from Plausible:
    // "our API docs are slightly wrong in that they are written
    // with server-side and proxy use-cases in mind. We do infer the IP
    // if it is not explicitly set"
    // 
    //let ipAddress = await getPublicIPAddress();
    //if (!ipAddress) {
    //  ipAddress = await getIPAddress();
    //}

    //info("recordAppPageView IP address: " + ipAddress + ", user agent: " + userAgent);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000 /* ms */);
    const url = "app://localhost/" + urlFragment;
    let referrer = null;
    if (isIOS()) {
      referrer = "https://www.apple.com/ios/";
    } else {
      referrer = "https://www.android.com/";
    }

    // https://plausible.io/docs/events-api
    const response = await fetch(
      "https://plausible.io/api/event",
      {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "User-Agent": userAgent,

          // See comments above
          //"X-Forwarded-For": ipAddress,
        },
        body: JSON.stringify({
          name: "pageview",
          domain: HOST,
          url: url,
          referrer: referrer,
          props: {
            isAppPageView: true,
            isAndroid: isAndroid(),
            isIOS: isIOS(),
          }
        }),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (response.status == 200 || response.status == 202) {
      info("recordAppPageView completed to " + url);
    } else {
      let plausibleError = "Plausible returned " + response.status + " for " + urlFragment + "\n\nResponse headers:";
      for (let header of response.headers.entries()) {
        plausibleError += "\n" + header[0]+ ': '+ header[1];
      }
      plausibleError += "\n\nResponse text:\n\n";
      plausibleError += await response.text();
      reportIssue(plausibleError);
    }

  } catch (e) {
    info("recordAppPageView error to " + urlFragment + ": " + getErrors(e));
  }
}

async function getUserAgent() {
  if (isAndroid() || isIOS()) {
    return await DeviceInfo.getUserAgent();
  } else if (isWeb()) {
    return window.navigator.userAgent;
  } else {
    throw new Error("Unknown platform");
  }
}

async function getIPAddress() {
  // https://docs.expo.dev/versions/latest/sdk/network/#networkgetipaddressasync
  return await Network.getIpAddressAsync();
}

async function getPublicIPAddress() {
  try {
    info("getPublicIPAddress started");

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000 /* ms */);
    const resp = await fetch(
      "https://api.ipify.org/?format=json",
      {
        signal: controller.signal,
      }
    );
    clearTimeout(timeoutId);

    info("getPublicIPAddress returned");

    const data = await resp.json();
    return data.ip;
  } catch (e) {
    info("getPublicIPAddress failed with: " + getErrors(e));
    return null;
  }
}

async function onFirstInitializedMap() {
  info("AppUtilities.onFirstInitializedMap Map is fully initialized");
}

const PRODUCT_ITEM_HEIGHT = 100;
const PRODUCT_ITEM_PADDING_VERTICAL = 10;
const PRODUCT_ITEM_HEIGHT_TOTAL = PRODUCT_ITEM_HEIGHT + PRODUCT_ITEM_PADDING_VERTICAL + PRODUCT_ITEM_PADDING_VERTICAL + StyleSheet.hairlineWidth;
const PRODUCT_THUMBNAIL_WIDTH = isWeb() ? PRODUCT_ITEM_HEIGHT : 75;
const PRODUCT_MAIN_FONT_SIZE = isWeb() ? 18 : 16;

const STYLES = StyleSheet.create({
  certificationImage: {
    maxHeight: isWeb() ? 70 : 60,
    marginRight: 10,
    marginLeft: 10,
  },
});

function productListItemLayout(data, index) {
  return {
    length: PRODUCT_ITEM_HEIGHT_TOTAL,
    offset: PRODUCT_ITEM_HEIGHT_TOTAL * index,
    index,
  };
}

function createItemRow(product, onPress, accessoryLeft) {
  return {
    i: product.id,
    title: product.product_name,
    description: product.producer,
    imageId: product.files && product.files.length > 0 ? product.files[0].id : null,
    price_amount: product.price_amount,
    price_details: product.price_details,
    details: product,
    onPress: onPress,
    accessoryLeft: accessoryLeft,
  };
}


// ls assets/certifications/width85/ | awk 'BEGIN { print "const certificationLogos = ["; } { img=$0; gsub(/\..*$/, "", img); map[0+(i++)] = img; print "  require(\"../../assets/certifications/width85/" $0 "\"),"; } END { print "];\n"; print "const certificationLogoOffsets = {"; for (key in map) { print "  " map[key] ": " key ","; } print "}"; }'
const certificationLogos = [
  require("../../assets/certifications/width85/agw_certified_grassfed.png"),
  require("../../assets/certifications/width85/agw_certified_non_gmo.png"),
  require("../../assets/certifications/width85/agw_certified_organic.png"),
  require("../../assets/certifications/width85/agw_certified_regenerative.png"),
  require("../../assets/certifications/width85/agw_salmon_welfare_certified.png"),
  require("../../assets/certifications/width85/american_grassfed_association.png"),
  require("../../assets/certifications/width85/american_humane_certified.png"),
  require("../../assets/certifications/width85/animal_welfare_approved.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_1.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_2.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_3.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_4.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_5.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_6.png"),
  require("../../assets/certifications/width85/animal_welfare_certified_fish_1.jpg"),
  require("../../assets/certifications/width85/animal_welfare_certified_fish_3.jpg"),
  require("../../assets/certifications/width85/animal_welfare_certified_fish_6.jpg"),
  require("../../assets/certifications/width85/applegate_humanely_raised.png"),
  require("../../assets/certifications/width85/certified_humane_free_range.png"),
  require("../../assets/certifications/width85/certified_humane_grass_fed.png"),
  require("../../assets/certifications/width85/certified_humane_pasture_raised_grass_fed.png"),
  require("../../assets/certifications/width85/certified_humane.png"),
  require("../../assets/certifications/width85/food_alliance_certified.png"),
  require("../../assets/certifications/width85/one_health_certified.png"),
  require("../../assets/certifications/width85/regenerative_organic_certified.png"),
  require("../../assets/certifications/width85/usda_organic.png"),
  require("../../assets/certifications/width85/better_chicken_project.png"),
  require("../../assets/certifications/width85/certified_humane_approved_hatcheries.png"),
];

const certificationLogoOffsets = {
  agw_certified_grassfed: 0,
  agw_certified_non_gmo: 1,
  agw_certified_organic: 2,
  agw_certified_regenerative: 3,
  agw_salmon_welfare_certified: 4,
  american_grassfed_association: 5,
  american_humane_certified: 6,
  animal_welfare_approved: 7,
  animal_welfare_certified_1: 8,
  animal_welfare_certified_2: 9,
  animal_welfare_certified_3: 10,
  animal_welfare_certified_4: 11,
  animal_welfare_certified_5: 12,
  animal_welfare_certified_6: 13,
  animal_welfare_certified_fish_1: 14,
  animal_welfare_certified_fish_3: 15,
  animal_welfare_certified_fish_6: 16,
  applegate_humanely_raised: 17,
  certified_humane_free_range: 18,
  certified_humane_grass_fed: 19,
  certified_humane_pasture_raised_grass_fed: 20,
  certified_humane: 21,
  food_alliance_certified: 22,
  one_health_certified: 23,
  regenerative_organic_certified: 24,
  usda_organic: 25,
  better_chicken_project: 26,
  certified_humane_approved_hatcheries: 27,
}

function renderProductRow(item, index, width) {
  const leftAccessory = item.accessoryLeft ? item.accessoryLeft() : null;
  const details = item.details;
  let images = [];
  if (details) {
    // NOTE: We only show some of these when horizontal space is limited
    if (details.animal_welfare_approved && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_approved" source={certificationLogos[certificationLogoOffsets["animal_welfare_approved"]]} style={STYLES.certificationImage} resizeMode="contain" />);

      if (details.agw_certified_grassfed && width >= AppConstants.SMALL_SCREEN_WIDTH1) {
        images.push(<Image key="agw_certified_grassfed" source={certificationLogos[certificationLogoOffsets["agw_certified_grassfed"]]} style={STYLES.certificationImage} resizeMode="contain" />);
      }
      if (details.agw_certified_non_gmo && width >= AppConstants.SMALL_SCREEN_WIDTH1) {
        images.push(<Image key="agw_certified_non_gmo" source={certificationLogos[certificationLogoOffsets["agw_certified_non_gmo"]]} style={STYLES.certificationImage} resizeMode="contain" />);
      }
      if (details.agw_certified_organic && width >= AppConstants.SMALL_SCREEN_WIDTH1) {
        images.push(<Image key="agw_certified_organic" source={certificationLogos[certificationLogoOffsets["agw_certified_organic"]]} style={STYLES.certificationImage} resizeMode="contain" />);
      }
      if (details.agw_certified_regenerative && width >= AppConstants.SMALL_SCREEN_WIDTH1) {
        images.push(<Image key="agw_certified_regenerative" source={certificationLogos[certificationLogoOffsets["agw_certified_regenerative"]]} style={STYLES.certificationImage} resizeMode="contain" />);
      }
      if (details.agw_salmon_welfare_certified && width >= AppConstants.SMALL_SCREEN_WIDTH1) {
        images.push(<Image key="agw_salmon_welfare_certified" source={certificationLogos[certificationLogoOffsets["agw_salmon_welfare_certified"]]} style={STYLES.certificationImage} resizeMode="contain" />);
      }
    }
    if (details.certified_humane_pasture_raised_grass_fed && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="certified_humane_pasture_raised_grass_fed" source={certificationLogos[certificationLogoOffsets["certified_humane_pasture_raised_grass_fed"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.certified_humane_grass_fed && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="certified_humane_grass_fed" source={certificationLogos[certificationLogoOffsets["certified_humane_grass_fed"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.certified_humane_free_range && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="certified_humane_free_range" source={certificationLogos[certificationLogoOffsets["certified_humane_free_range"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.certified_humane && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="certified_humane" source={certificationLogos[certificationLogoOffsets["certified_humane"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    }
    if (details.certified_humane_approved_hatcheries && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="certified_humane_approved_hatcheries" source={certificationLogos[certificationLogoOffsets["certified_humane_approved_hatcheries"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    }
    if (details.animal_welfare_certified_6 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_6" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_6"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_5 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_5" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_5"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_4 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_4" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_4"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_3 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_3" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_3"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_2 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_2" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_2"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_1 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_1" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_1"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    }
    if (details.animal_welfare_certified_fish_6 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_fish_6" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_fish_6"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_fish_3 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_fish_3" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_fish_3"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    } else if (details.animal_welfare_certified_fish_1 && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="animal_welfare_certified_fish_1" source={certificationLogos[certificationLogoOffsets["animal_welfare_certified_fish_1"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    }
    if (details.better_chicken_project && (images.length == 0 || width >= AppConstants.SMALL_SCREEN_WIDTH1)) {
      images.push(<Image key="better_chicken_project" source={certificationLogos[certificationLogoOffsets["better_chicken_project"]]} style={STYLES.certificationImage} resizeMode="contain" />);
    }
  }

  return (
    <Pressable key={item.i} onPress={item.onPress} style={{ height: PRODUCT_ITEM_HEIGHT + PRODUCT_ITEM_PADDING_VERTICAL + PRODUCT_ITEM_PADDING_VERTICAL }}>
      <View style={{ flex: 1, flexDirection: "row", alignItems: "center", backgroundColor: "#ffffff", padding: PRODUCT_ITEM_PADDING_VERTICAL }}>
        {
          details ?
              <View style={{ width: PRODUCT_THUMBNAIL_WIDTH, height: PRODUCT_ITEM_HEIGHT, alignItems: "center", marginRight: 20, }}>
                {
                  item.imageId ?
                      <Image
                        source={{ uri: getRootUri() + "find_humane/api/get_product_file_thumbnail?product_id=" + item.i + "&file_id=" + item.imageId }}
                        style={{
                          alignSelf: 'stretch',
                          flex: 1,
                        }}
                        resizeMode="contain"
                      />
                    :
                      <Image source={require("../../assets/No-Image-Placeholder.png")} style={{ height: PRODUCT_ITEM_HEIGHT }} resizeMode="contain" />
                }
              </View>
            :
              null
        }
        {
          leftAccessory ?
              <View style={AppConstants.STYLES.paddingRight10}>
                {
                  leftAccessory
                }
              </View>
            :
              null
        }
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <Text style={{ flexWrap: 'wrap', fontSize: PRODUCT_MAIN_FONT_SIZE, fontWeight: "800", paddingBottom: 3 }}>{item.title}</Text>
          </View>
          {
            item.price_amount || (details && details.price_details) ?
                <View style={{ flexDirection: 'row' }}>
                  <Text style={{ flexWrap: 'wrap', fontSize: PRODUCT_MAIN_FONT_SIZE - 4, fontWeight: "600" }}>{item.price_amount ? item.price_amount : details.price_details}</Text>
                </View>
              :
                null
          }
          {
            item.description ?
                <View style={{ flexDirection: 'row' }}>
                  <Text style={{ flexWrap: 'wrap', fontSize: PRODUCT_MAIN_FONT_SIZE - 6, fontWeight: "400" }}>{item.description}</Text>
                </View>
              :
                null
          }
        </View>
        <View style={{ flexDirection: "row", flexShrink: 1, justifyContent: "flex-end", alignItems: "center" }}>
          { images }
        </View>
      </View>
    </Pressable>
  );
}

// Assume the srcset is sorted by scale
function selectImageFromSrcSet(srcset) {
  const sources = srcset.split(", ");
  const originalPixelRatio = PixelRatio.get();
  // At least in Firefox on mobile, 2.5 was rounded up to 3 by <img srcset />
  const comparisonRatio = Math.ceil(originalPixelRatio);
  info("selectImageFromSrcSet pixelRatio: " + originalPixelRatio + ", comparisonRatio: " + comparisonRatio + ", searching: " + srcset);
  let result = null;
  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    const space = source.lastIndexOf(' ');
    const uri = source.substring(0, space);
    const scale = source.substring(space + 1);
    if (scale.endsWith("x")) {
      const sourceScale = Number.parseFloat(scale.substring(0, scale.length - 1));
      //info("selectImageFromSrcSet uri: " + uri + ", scale: " + scale + ", sourceScale: " + sourceScale);
      if (sourceScale <= comparisonRatio) {
        result = uri;
      } else {
        break;
      }
    } else {
      info("Unknown scale " + scale);
    }
  }
  info("selectImageFromSrcSet result: <" + result + ">");
  return result;
}

function getCertificationImage(filter, style) {
  const offset = certificationLogoOffsets[filter];
  if (DEBUGLEVEL.certifications) {
    debug("AppUtilities.getCertificationImage filter: " + filter + ", offset: " + offset);
  }
  if (isDefined(offset)) {
    const src = certificationLogos[certificationLogoOffsets[filter]];
    if (DEBUGLEVEL.certifications) {
      debug("AppUtilities.getCertificationImage src: ");
      dir(src);
    }
    if (src) {
      const cert = AppConstants.FILTER_TO_CERTIFICATION_MAP[filter];
      if (DEBUGLEVEL.certifications) {
        debug("AppUtilities.getCertificationImage cert: " + cert);
      }
      const link = AppConstants.CERTIFICATION_LINKS[cert];
      if (DEBUGLEVEL.certifications) {
        debug("AppUtilities.getCertificationImage link: " + link);
      }
      return createLink(
               <Image source={src} style={style} />,
               link,
               AppConstants.UTM_CAMPAIGNS.PRODUCT_DETAILS,
               null,
               null,
               null,
               filter
             );
    } else {
      return null;
    }
  } else {
    return null;
  }
}

function postProcessResultsLocations(locationsJSON) {
  // See FindHumane::Utilities.postProcessSearchResultsDetailed
  if (DEBUGLEVEL.location) debug("> postProcessResultsLocations");
  const locationNames = locationsJSON.names;
  const locations = {};
  for (const location of locationsJSON.locations) {
    const id = location[0];
    const locationObj = {
      i: id,
      l: location[1],
      g: location[2],
      t: locationNames[location[3]],
    };
    if (location.length >= 5 && location[4] < AppConstants.MAP_PIN_TYPES.length) {
      locationObj.p = location[4];
    }
    locations["" + id] = locationObj;
  }
  if (DEBUGLEVEL.location) debug("< postProcessResultsLocations");
  return locations;
}

export {
  DEBUGLEVEL,
  LABEL_GUIDES,
  info,
  debug,
  dir,
  error,
  isAndroid,
  isIOS,
  isWeb,
  isWebAndroid,
  isWebiOS,
  handleException,
  fetchAuthorized,
  fetchUnauthorized,
  handleErrorStatus,
  isStatusSuccess,
  toJSONPretty,
  getHeight,
  getWidth,
  sleep,
  login,
  logout,
  getErrors,
  getAccumulatedDebug,
  loadLocalData,
  updateLocalData,
  getLocalData,
  getAllLocalData,
  getUUID,
  getLocalDataRemoteSession,
  createRequestUrl,
  randomInt,
  getHeaderRight,
  getRootUri,
  sendDebugDirect,
  openLink,
  createLink,
  createInternalLink,
  createNavigationLink,
  setStoredValue,
  removeStoredValue,
  removeLocalData,
  reportIssue,
  debugObject,
  isUndefined,
  isDefined,
  isNull,
  isBlank,
  isString,
  isNullOrUndefined,
  escapeHTML,
  getLinkWithUTM,
  filterDown,
  getZoomLevelForGeolocationTypes,
  checkSearchResults,
  isMarkdownLink,
  getMarkdownLink,
  getMarkdownLinkText,
  onFirstInitializedMap,
  renderProductRow,
  productListItemLayout,
  createItemRow,
  selectImageFromSrcSet,
  getCertificationImage,
  setExtraDebug,
  postProcessResultsLocations,
  certificationLogos,
  certificationLogoOffsets,
  getMainDataLink,
  getExploreLink,
  convertFilterHashToQueryParametersSorted,
  getDefaultFilterOutdoorsLink,
  getDefaultFilterPastureLink,
  getDefaultFilterIndoorsLink,
  isDefaultFilter,
  getDefaultFilterLink,
  getDefaultFilterExploreLink,
  getDefaultFilterExploreOutdoorsLink,
  getDefaultFilterExplorePastureLink,
  getDefaultFilterExploreIndoorsLink,
  updateSavedAppState,
  getSavedAppState,
  recordAppPageView,
}
